import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { Paperclip, Upload, X, FileText, Plus, Trash2 } from "lucide-react"
import { Card, Button, Select } from "../../ui"
import { useUploadOrderDocumentMutation } from "shared/redux/endpoints/procurementApprovals"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import {
  ORDER_DOC_TYPE_OPTIONS,
  ORDER_DOC_MAX_SIZE_BYTES,
  ORDER_DOC_ALLOWED_EXTENSIONS,
  formatDate,
} from "../../constants"

const ACCEPT = ORDER_DOC_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")

let rowSeq = 0
const emptyRow = () => ({ key: `row-${(rowSeq += 1)}`, docType: ORDER_DOC_TYPE_OPTIONS[0].value, file: null })

const formatBytes = (n) => {
  if (n == null) return ""
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

const validateFile = (file) => {
  const ext = file.name.split(".").pop()?.toLowerCase()
  if (!ext || !ORDER_DOC_ALLOWED_EXTENSIONS.includes(ext)) {
    return `File type not allowed. Accepted: ${ORDER_DOC_ALLOWED_EXTENSIONS.join(", ")}.`
  }
  if (file.size > ORDER_DOC_MAX_SIZE_BYTES) {
    return "File is larger than the 10MB limit."
  }
  return null
}

// Styled trigger for a hidden native file input — the browser's raw "Choose
// File" control never shows. Same single-file `onChange` contract as before.
const FilePicker = ({ file, disabled, onSelect }) => {
  const ref = useRef(null)
  return (
    <div className="pc-filepick">
      <button
        type="button"
        className="pc-btn pc-btn--secondary"
        disabled={disabled}
        onClick={() => ref.current?.click()}
      >
        <Paperclip aria-hidden />
        {file ? "Change file" : "Select file"}
      </button>
      <span className={`pc-filepick-name${file ? " pc-filepick-name--set" : ""}`} title={file?.name}>
        {file ? file.name : "No file selected"}
      </span>
      <input
        ref={ref}
        type="file"
        hidden
        accept={ACCEPT}
        disabled={disabled}
        onChange={(e) => onSelect(e.target.files?.[0] || null)}
      />
    </div>
  )
}

// Attach documents to an order request — GET .../order-requests/{id} already
// returns `documents[]`, so real uploads just refetch that via cache
// invalidation. Each row uploads independently (its own API call, its own
// docType) — "+ Add file" adds another row so several files can be queued up
// and sent one at a time without waiting on each other. Two modes depending
// on whether the order has an id yet:
//   - `orderId` set: a row's Upload button calls the API immediately.
//   - `orderId` null (brand-new, unsaved form): nothing to upload *to* yet,
//     so a row's Upload button hands it to the parent via `onStage` instead,
//     shown as a separate "will upload after you save" queue via `stagedFiles`.
//
// Exposes `uploadPending()` via ref — uploads every row that already has a
// file picked but whose own "Upload" button was never clicked. Lets
// OrderDetailContainer's "Upload & Resubmit" implicitly pick up whatever the
// requester staged here instead of asking them to pick a file a second time
// in the resubmit modal.
const DocumentUploader = forwardRef(
  ({ orderId, documents, canUpload = true, onStage, stagedFiles, onRemoveStaged }, ref) => {
    const [uploadDocument] = useUploadOrderDocumentMutation()
    const [rows, setRows] = useState([emptyRow()])
    const [uploadingKey, setUploadingKey] = useState(null)

    const addRow = () => setRows((prev) => [...prev, emptyRow()])
    const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key))
    const patchRow = (key, patch) =>
      setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))

    // Returns true on success (or if there was nothing to upload), false if
    // the upload failed — callers use this to decide whether it's safe to
    // proceed (e.g. resubmit).
    const handleUploadRow = async (row) => {
      if (!row.file) return true
      const error = validateFile(row.file)
      if (error) {
        showToastError(error)
        return false
      }

      if (!orderId) {
        onStage?.(row.file, row.docType)
        removeRow(row.key)
        return true
      }

      const formData = new FormData()
      formData.append("file", row.file)
      formData.append("docType", row.docType)
      setUploadingKey(row.key)
      try {
        await uploadDocument({ id: orderId, formData }).unwrap()
        showToastSuccess("Document uploaded.")
        removeRow(row.key)
        return true
      } catch (err) {
        showToastError(getApiErrorMessage(err, "Couldn't upload this file. Please try again."))
        return false
      } finally {
        setUploadingKey(null)
      }
    }

    useImperativeHandle(ref, () => ({
      uploadPending: async () => {
        const staged = rows.filter((r) => r.file)
        for (const row of staged) {
          // eslint-disable-next-line no-await-in-loop
          const ok = await handleUploadRow(row)
          if (!ok) return false
        }
        return true
      },
    }))

    const docs = documents || []
    const pending = stagedFiles || []

  return (
    <Card
      icon={<Paperclip aria-hidden />}
      title="Attachments"
      subtitle="Supporting files for this order — quotes, contracts, receipts."
      tone="purple"
      className="pc-attachments"
      action={
        canUpload ? (
          <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={addRow}>
            Add file
          </Button>
        ) : null
      }
    >
      {canUpload && (
        <div className="pc-upload-rows">
          {rows.map((row) => (
            <div key={row.key} className="pc-upload-row">
              <div className="pc-upload-type">
                <Select
                  label="Document type"
                  value={row.docType}
                  allowEmpty={false}
                  onChange={(v) => patchRow(row.key, { docType: v })}
                  options={ORDER_DOC_TYPE_OPTIONS}
                />
              </div>
              <div className="pc-upload-file">
                <label className="pc-label">File</label>
                <FilePicker
                  file={row.file}
                  disabled={uploadingKey === row.key}
                  onSelect={(file) => patchRow(row.key, { file })}
                />
              </div>
              <div className="pc-upload-actions">
                <Button
                  variant="primary"
                  disabled={!row.file || uploadingKey === row.key}
                  onClick={() => handleUploadRow(row)}
                >
                  {uploadingKey === row.key ? "Uploading…" : "Upload"}
                </Button>
                {rows.length > 1 && (
                  <Button
                    variant="ghost"
                    leftIcon={<Trash2 aria-hidden />}
                    disabled={uploadingKey === row.key}
                    onClick={() => removeRow(row.key)}
                    style={{ color: "var(--pc-danger)" }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="pc-attach-group">
          <div className="pc-attach-group-title">Will upload after you save</div>
          {pending.map((p, idx) => (
            <div key={idx} className="pc-attach-row pc-attach-row--staged">
              <span className="pc-attach-ico">
                <FileText aria-hidden />
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="pc-attach-name">{p.file.name}</div>
                <div className="pc-attach-type">
                  {ORDER_DOC_TYPE_OPTIONS.find((o) => o.value === p.docType)?.label || p.docType}
                </div>
              </div>
              {onRemoveStaged && (
                <button
                  type="button"
                  className="pc-attach-remove"
                  onClick={() => onRemoveStaged(idx)}
                  aria-label="Remove"
                >
                  <X size={16} aria-hidden />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {docs.length === 0 ? (
        pending.length === 0 && <p className="pc-section-empty">No documents attached.</p>
      ) : (
        <div className="pc-attach-group">
          <div className="pc-attach-group-title">Attached documents</div>
          {docs.map((d, i) => (
            <a
              key={d.id ?? i}
              className="pc-attach-row"
              href={d.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="pc-attach-ico">
                <Upload aria-hidden />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="pc-attach-name">{d.fileName || "Document"}</div>
                <div className="pc-attach-type">
                  {(ORDER_DOC_TYPE_OPTIONS.find((o) => o.value === d.docType)?.label || d.docType || "File")}
                  {d.sizeBytes ? ` · ${formatBytes(d.sizeBytes)}` : ""}
                  {d.uploadedAt ? ` · ${formatDate(d.uploadedAt)}` : ""}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </Card>
    )
  }
)

DocumentUploader.displayName = "DocumentUploader"

export default DocumentUploader
